import { useEffect, useMemo, useState } from 'react';

import type { FindingOut } from '@/api/types';

// Practitioner-priority ordering: pending first (the work queue), then
// needs_review (un-dispositioned but already touched), then approved,
// then rejected.
const STATUS_RANK: Record<string, number> = {
  pending: 0,
  needs_review: 1,
  approved: 2,
  rejected: 3,
};

function compareFindings(a: FindingOut, b: FindingOut): number {
  const sr = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
  if (sr !== 0) return sr;
  // Single-currency assumption: ranking by raw amount across mixed
  // currencies (e.g. $1,000 USD vs $1,500 CAD) is meaningless. Today's
  // engagements are mono-currency CAD so this is correct; mixed-
  // currency runs need a per-currency bucket + FX-aware rank.
  return Number(b.recovery.amount) - Number(a.recovery.amount);
}

/**
 * Queue order that is locked per filter state: membership + position are
 * derived once (filter + sort) when `filterKey` changes and then held
 * stable across data refetches, while each card's fields stay live via
 * id lookup into `findings`.
 *
 * The lock is what makes approve/reject advance by exactly one card
 * (INP-328 / INP-373): a disposition flips the finding's status, the
 * refetch would otherwise drop it from a status-filtered queue (or
 * re-rank it to the back), and every later card would shift one position
 * under an index-based cursor. Instead the just-reviewed card keeps its
 * slot — navigating back shows it with its new badge — until the
 * practitioner commits a filter change, which restarts the queue.
 */
export function useLockedFindingOrder(
  findings: FindingOut[],
  filtered: FindingOut[],
  filterKey: string,
): FindingOut[] {
  const findingById = useMemo(
    () => new Map(findings.map((f) => [f.finding_id, f])),
    [findings],
  );

  const freshOrder = useMemo(
    () => [...filtered].sort(compareFindings).map((f) => f.finding_id),
    [filtered],
  );

  const [locked, setLocked] = useState<{ key: string; ids: string[] } | null>(
    null,
  );

  // Adopt a fresh order whenever there is no lock for the current
  // filterKey. The non-empty guard covers the initial load (findings
  // arrive async after first render) — until then the render path
  // falls back to freshOrder, so nothing flickers.
  useEffect(() => {
    if (locked?.key !== filterKey && freshOrder.length > 0) {
      setLocked({ key: filterKey, ids: freshOrder });
    }
  }, [locked, filterKey, freshOrder]);

  return useMemo(() => {
    const ids = locked?.key === filterKey ? locked.ids : freshOrder;
    const out: FindingOut[] = [];
    for (const id of ids) {
      const f = findingById.get(id);
      if (f) out.push(f);
    }
    return out;
  }, [locked, filterKey, freshOrder, findingById]);
}
